import { memo, useMemo, useState, useEffect } from 'react';
import { ENABLE_WINTER_SNOW } from '@/config/seasonalEffects';

interface Snowflake {
  id: number;
  left: number;
  size: number;
  opacity: number;
  duration: number;
  delay: number;
  rotate: number;
  drift: number;
}

/**
 * Snowfall Effect Component
 * 
 * Beautiful decorative winter snow effect with minimal CPU impact.
 * - Uses CSS animations only (no JS animation loop)
 * - pointer-events: none (doesn't block interactions)
 * - z-index: 1 (above background, below content)
 * - 25 beautiful snowflakes with varying properties
 * - Can be disabled by user in settings
 */
const Snowfall = memo(function Snowfall() {
  const [userEnabled, setUserEnabled] = useState(true);

  // Generate snowflakes once on mount - MUST be before any conditional returns
  const snowflakes = useMemo<Snowflake[]>(() => {
    const flakes: Snowflake[] = [];
    const count = 25;

    for (let i = 0; i < count; i++) {
      flakes.push({
        id: i,
        left: Math.random() * 100,
        size: 12 + Math.random() * 16,
        opacity: 0.5 + Math.random() * 0.4,
        duration: 10 + Math.random() * 8,
        delay: Math.random() * 10,
        rotate: Math.random() * 360,
        drift: -30 + Math.random() * 60,
      });
    }

    return flakes;
  }, []);

  // Check user setting from localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('userSettings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        if (parsed.showSnowfall === false) {
          setUserEnabled(false);
        }
      } catch {
        // ignore
      }
    }

    const handleStorage = () => {
      const settings = localStorage.getItem('userSettings');
      if (settings) {
        try {
          const parsed = JSON.parse(settings);
          setUserEnabled(parsed.showSnowfall !== false);
        } catch {
          // ignore
        }
      }
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('settingsChanged', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('settingsChanged', handleStorage);
    };
  }, []);

  // Don't render if global setting or user setting disabled
  if (!ENABLE_WINTER_SNOW || !userEnabled) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 overflow-hidden pointer-events-none"
      style={{ zIndex: 1 }}
      aria-hidden="true"
    >
      {snowflakes.map((flake) => (
        <div
          key={flake.id}
          className="absolute select-none"
          style={{
            left: `${flake.left}%`,
            fontSize: `${flake.size}px`,
            opacity: flake.opacity,
            animation: `snowfall-${flake.id % 3} ${flake.duration}s linear ${flake.delay}s infinite`,
            transform: `rotate(${flake.rotate}deg)`,
            willChange: 'transform',
            filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.8))',
            color: 'white',
            textShadow: '0 0 4px rgba(173, 216, 230, 0.8), 0 0 8px rgba(135, 206, 250, 0.5)',
          }}
        >
          ❄
        </div>
      ))}

      <style>{`
        @keyframes snowfall-0 {
          0% {
            transform: translateY(-30px) translateX(0) rotate(0deg);
          }
          50% {
            transform: translateY(50vh) translateX(15px) rotate(180deg);
          }
          100% {
            transform: translateY(100vh) translateX(-10px) rotate(360deg);
          }
        }
        @keyframes snowfall-1 {
          0% {
            transform: translateY(-30px) translateX(0) rotate(0deg);
          }
          50% {
            transform: translateY(50vh) translateX(-20px) rotate(-180deg);
          }
          100% {
            transform: translateY(100vh) translateX(10px) rotate(-360deg);
          }
        }
        @keyframes snowfall-2 {
          0% {
            transform: translateY(-30px) translateX(0) rotate(0deg);
          }
          33% {
            transform: translateY(33vh) translateX(10px) rotate(120deg);
          }
          66% {
            transform: translateY(66vh) translateX(-15px) rotate(240deg);
          }
          100% {
            transform: translateY(100vh) translateX(5px) rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
});

export default Snowfall;
